<script setup lang="ts">
import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';

import type { RouteName } from '@/router/routes';

defineProps<{
  canEditBilling?: boolean;
  canEditUsers?: boolean;
  seatAllocated?: boolean;
}>();

const visible = defineModel<boolean>('visible', { required: true });

const { t } = useI18n();
const router = useRouter();

async function navigate(name: RouteName) {
  visible.value = false;
  await router.push({ name });
}

function navigateToUsers() {
  return navigate('UserManagement');
}

function navigateToBilling() {
  return navigate('Billing');
}
</script>

<template>
  <i-dialog
    v-model:visible="visible"
    class="mb-8!"
    position="bottom"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <fa-icon :icon="fasTriangleExclamation" class="text-yellow-500" size="xl" />
        <span class="text-xl font-bold" data-test="seat-warning-dialog-title">{{ t('components.seatWarningDialog.moreUsersThanSeats') }}</span>
      </div>
    </template>

    <template #default>
      <div>{{ t('components.seatWarningDialog.userCountExceedsSeatCount') }}</div>
      <div>{{ t('components.seatWarningDialog.additionalSeatsRequired') }}</div>
      <div v-if="!seatAllocated" class="mt-2 text-warn font-bold">
        {{ t('components.seatWarningDialog.accessRestricted') }}
      </div>
    </template>

    <template #footer>
      <template v-if="seatAllocated">
        <i-button
          text
          :label="t('components.seatWarningDialog.later')"
          @click="visible = false"
        />

        <i-button
          v-if="canEditUsers"
          outlined
          :label="t('components.seatWarningDialog.manageUsers')"
          :icon="fasUser"
          @click="navigateToUsers"
        />

        <i-button
          v-if="canEditBilling"
          outlined
          :label="t('components.seatWarningDialog.manageSeats')"
          :icon="fasLandmark"
          @click="navigateToBilling"
        />
      </template>

      <i-button
        v-else
        outlined
        :label="t('components.seatWarningDialog.understood')"
        @click="visible = false"
      />
    </template>
  </i-dialog>
</template>
